import * as React from "react";
import { Spinner, Text } from "@fluentui/react-components";
import PDFViewer from "./PDFViewer";
import { fetchFastApi } from "../utils/fastApi";
import { CustomXmlPart, CustomXmlPartSchema } from "../models/heretic/customXmlPart";
import { SourceDocument } from "../models/heretic/sourceDocument";
import { CheckResult, CheckResultSchema } from "../models/heretic/checkResult";
import { CheckResultCard } from "./heretic/checkResultCard";
import { ProcedureCheck, ProcedureCheckSchema } from "../models/heretic/procedureCheck";
import { useEffect, useMemo } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { pdfDocument } from "../models/heretic/pdfDocument";
import { z } from "zod";
import { SessionFileSearch } from "./SessionFileSearch";
import { SessionPlainTextSearch } from "./SessionPlainTextSearch";
import { useAudit } from "../contexts/auditSessionContext";
import { UploadFilesToSession } from "./heretic/uploadFilesToSession";
import { xmlToJson, jsonToXmlString } from "../utils/xml_utils";
import {
  AuditSession,
  evaluatedColumnSchema,
  matchItemSchema,
  UploadedDocumentSchema,
} from "../models/heretic/auditSession";
import { v4 as uuidv4 } from "uuid";
import { useSession } from "../contexts/sessionContext";
import CSVViewer from "./CSVViewer";
import { ImageViewer } from "./ImageViewer";

interface PresignedUrlInfo {
  url: string;
  expiresAt: number; // Unix timestamp in milliseconds
  documentExtraction: z.infer<typeof pdfDocument>;
  document: z.infer<typeof UploadedDocumentSchema>;
}

export const NamedRangeReader: React.FC = () => {
  const [metadata, setMetadata] = React.useState<CustomXmlPart | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const presignedUrlsMapRef = React.useRef<Record<string, PresignedUrlInfo>>({});
  const [currentDocument, setCurrentDocument] = React.useState<PresignedUrlInfo | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentCellsSourceRowId, setCurrentCellsSourceRowId] = React.useState<string | null>(null);
  const { loading: sessionXmlLoading } = useSession();

  const [currentChecks, setCurrentChecks] = React.useState<{
    checkResults: CheckResult[];
    procedureChecks: ProcedureCheck[];
    evalColumn?: z.infer<typeof evaluatedColumnSchema>;
    checkId?: string;
    evalColumnId?: string;
  } | null>(null);
  const { auditSession, setAuditSession, loading: auditSessionLoading } = useAudit();

  console.log("auditSession", auditSession);
  // Helper function to check if a URL has expired
  const isUrlExpired = (expiresAt: number): boolean => {
    // Add a buffer of 5 minutes to prevent edge cases
    const bufferMs = 5 * 60 * 1000;
    return Date.now() + bufferMs > expiresAt;
  };

  const documentIdToDocumentMap = useMemo(() => {
    return auditSession?.documents.reduce(
      (acc, doc) => {
        acc[doc.documentId] = doc;
        return acc;
      },
      {} as Record<string, AuditSession["documents"][number]>
    );
  }, [auditSession]);

  const handleAddDocumentToSourceRow = async (documentId: string) => {
    console.log("currentCellsSourceRowId in handleAddDocumentToSourceRow", currentCellsSourceRowId);
    if (!currentCellsSourceRowId || !auditSession) {
      console.log("No source row id or audit session found");
      return;
    }
    const hasDocumentInSourceRow = auditSession.sourceRows
      ?.find((sourceRow) => sourceRow.id === currentCellsSourceRowId)
      ?.matchItems?.some((document) => document.sourceDocument.id === documentId);
    if (hasDocumentInSourceRow) {
      console.log("Document already in source row");
      return;
    }
    try {
      const response = await fetchFastApi({
        method: "POST",
        path: `/matching/match-session/${auditSession.id}/source-row/${currentCellsSourceRowId}/create-match-item`,
        body: {
          documentId: documentId,
          procedureDocumentIds: [],
        },
      });
      const parsedResponse = matchItemSchema.parse(response.matchItem);
      setAuditSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sourceRows: prev.sourceRows?.map((sourceRow) =>
            sourceRow.id === currentCellsSourceRowId
              ? {
                  ...sourceRow,
                  matchItems: [...(sourceRow.matchItems || []), parsedResponse],
                }
              : sourceRow
          ),
        };
      });
    } catch (error) {
      console.error("Error adding document to source row:", error);
      setError("Error adding document to source row");
    }
  };

  // Function to fetch presigned URL for a document
  const fetch_presigned_url = async (file_id: string) => {
    try {
      console.log("file_id", file_id);
      const document = documentIdToDocumentMap?.[file_id];
      const response = await fetchFastApi({
        path: `/matching/session/${auditSession?.id}/session-document/${document?.id}`,
        method: "GET",
      });
      console.log("response", response);
      const url = response.documentUrlInfo.url;

      // Handle case where extractionUrlInfo might be missing in the response
      let parsedDocument = null;
      try {
        if (response.extractionUrlInfo) {
          parsedDocument = pdfDocument.parse(response.extractionUrlInfo);
        } else {
          console.log("No extractionUrlInfo in response, using default values");
          // Create a minimal default document extraction object
          parsedDocument = {
            id: file_id,
            type: "pdf",
            pages: [],
            title: "Unknown Document",
            normalized_title: "unknown_document",
          };
        }
      } catch (e) {
        console.error("Error parsing document extraction:", e);
        // Provide a fallback extraction object
        parsedDocument = {
          id: file_id,
          type: "pdf",
          pages: [],
          title: "Error Parsing Document",
          normalized_title: "error_parsing_document",
        };
      }

      // Extract expiration timestamp from URL
      const expiresParam = new URL(url).searchParams.get("Expires");
      const expiresAt = expiresParam ? parseInt(expiresParam) * 1000 : 0; // Convert to milliseconds

      return { url, expiresAt, documentExtraction: parsedDocument, document: response.document };
    } catch (error) {
      console.error("Error fetching presigned URL:", error, file_id);
      setError("Error fetching presigned URL");
      return null;
    }
  };

  const getCurrentCellsSourceRowId = async ({
    currentRangeAddress,
    rangeNamesAndAddresses,
    namedRangeAddressToXmlPart,
  }: {
    currentRangeAddress: string;
    rangeNamesAndAddresses: Record<string, string>;
    namedRangeAddressToXmlPart: Record<string, CustomXmlPart>;
  }) => {
    // Find which range contains the current cell
    const currentCell = currentRangeAddress;
    const matchingRange = Object.keys(rangeNamesAndAddresses)
      .filter((namedRange) => namedRange.startsWith("msrow"))
      .find((namedRange) => {
        // Parse the range string (e.g., "Summary!A2:B2")
        const [, cells] = rangeNamesAndAddresses[namedRange].split("!");
        const [startCell, endCell] = cells.split(":");

        // If it's a single cell range, check for exact match
        if (!endCell) {
          return startCell === currentCell.split("!")[1];
        }

        // For multi-cell ranges, check if current cell is within the range
        const currentCellRef = currentCell.split("!")[1];
        const currentCol = currentCellRef.match(/[A-Z]+/)[0];
        const currentRow = parseInt(currentCellRef.match(/\d+/)[0]);

        const startCol = startCell.match(/[A-Z]+/)[0];
        const startRow = parseInt(startCell.match(/\d+/)[0]);
        const endCol = endCell.match(/[A-Z]+/)[0];
        const endRow = parseInt(endCell.match(/\d+/)[0]);

        // Convert column letters to numbers for comparison
        const colToNum = (col) => {
          let num = 0;
          for (let i = 0; i < col.length; i++) {
            num = num * 26 + (col.charCodeAt(i) - 64);
          }
          return num;
        };

        const currentColNum = colToNum(currentCol);
        const startColNum = colToNum(startCol);
        const endColNum = colToNum(endCol);

        return (
          currentColNum >= startColNum && currentColNum <= endColNum && currentRow >= startRow && currentRow <= endRow
        );
      });

    try {
      const sourceRowId = namedRangeAddressToXmlPart[matchingRange]?.metadata?.source_row_id;
      console.log("sourceRowId", sourceRowId);
      setCurrentCellsSourceRowId(sourceRowId);
    } catch (error) {
      console.error("Error setting current cells source row id:", error);
      setCurrentCellsSourceRowId(null);
    }
  };

  // Function to fetch check results
  const fetch_check_result = async ({
    source_row_id,

    check_id,
    eval_column_id,
  }: {
    source_row_id: string;
    check_id?: string;
    eval_column_id?: string;
  }) => {
    try {
      if (check_id) {
        const response = await fetchFastApi({
          path: `/matching/source-row/${source_row_id}/check-result/${check_id}`,
          method: "GET",
        });
        const parsedCheckResults = CheckResultSchema.array().parse(response?.checkResults);
        const parsedProcedureChecks = ProcedureCheckSchema.array().parse(response?.procedureChecks);
        return { checkResults: parsedCheckResults, procedureChecks: parsedProcedureChecks };
      } else if (eval_column_id) {
        const response = await fetchFastApi({
          path: `/matching/source-row/${source_row_id}/check-result/eval-column/${eval_column_id}`,
          method: "GET",
        });
        const parsedCheckResults = CheckResultSchema.array().parse(response?.checkResults);
        const parsedEvalColumn = evaluatedColumnSchema.parse(response?.column);
        return { checkResults: parsedCheckResults, evalColumn: parsedEvalColumn };
      }
    } catch (error) {
      console.error("Error fetching check result:", error);
      setError("Error fetching check result");
      return null;
    }
  };

  // Function to handle URL fetching
  const handleFileSelection = async (fileId: string) => {
    if (fileId) {
      const existingUrlInfo = presignedUrlsMapRef.current[fileId];

      if (!existingUrlInfo || isUrlExpired(existingUrlInfo.expiresAt)) {
        const urlInfo = await fetch_presigned_url(fileId);
        if (urlInfo) {
          presignedUrlsMapRef.current[fileId] = urlInfo;
          setCurrentDocument(urlInfo);
        }
      } else {
        setCurrentDocument(existingUrlInfo);
      }
    }
  };

  // Function to handle check result fetching
  const handleCheckResultFetching = async (foundMetadata: CustomXmlPart | null) => {
    if (foundMetadata?.test_result?.check_id && foundMetadata?.test_result?.sample_id) {
      const { checkResults: checkResultsFromApi, procedureChecks: procedureChecksFromApi } = await fetch_check_result({
        source_row_id: foundMetadata.metadata.source_row_id,
        check_id: foundMetadata.test_result.check_id,
      });
      if (checkResultsFromApi) {
        setCurrentDocument(null);
        setCurrentChecks({
          checkResults: checkResultsFromApi,
          procedureChecks: procedureChecksFromApi,
          checkId: foundMetadata.test_result.check_id,
        });
      }
    } else if (foundMetadata?.test_result?.eval_column_id && foundMetadata?.test_result?.sample_id) {
      const { checkResults: checkResultsFromApi, evalColumn: evalColumnFromApi } = await fetch_check_result({
        source_row_id: foundMetadata.test_result.sample_id,
        eval_column_id: foundMetadata.test_result.eval_column_id,
      });
      if (checkResultsFromApi) {
        setCurrentDocument(null);
        setCurrentChecks({
          checkResults: checkResultsFromApi,
          procedureChecks: [],
          evalColumn: evalColumnFromApi,
          evalColumnId: foundMetadata.test_result.eval_column_id,
        });
      }
    }
  };

  // Function to update XML part with new bounding box information
  const updateXmlPartWithBoundingBox = async (
    updatedMetadata: CustomXmlPart,
    snippedArea: {
      content?: string;
      bounding_box: [number, number][];
      pageNumber: number;
      page_width: number;
      page_height: number;
    }
  ) => {
    await Excel.run(async (context) => {
      if (!metadata) {
        const range = context.workbook.getSelectedRange();
        range.load("text");
        await context.sync();
        console.log("No metadata found");
        // create a new metadata part
        const uuid = uuidv4();
        const rangeId = `ms${uuid.replace(/-/g, "")}`;
        const newMetadata: CustomXmlPart = {
          id: rangeId,
          type: "midship_cell",
          createdAt: new Date().toISOString(),
          source_document: {
            id: currentDocument?.documentExtraction.id,
            name: currentDocument?.documentExtraction.filename,
            page_number: snippedArea.pageNumber,
            page_width: snippedArea.page_width,
            page_height: snippedArea.page_height,
            bounding_box: snippedArea.bounding_box,
          },
          value: "",
        };

        const namedRange = context.workbook.names.add(rangeId, range);

        const newMetadataXml = jsonToXmlString(newMetadata as any, "metadata", "http://app.midship.ai", true, true);
        console.log("newMetadataXml", newMetadataXml);
        const newMetadataPart = context.workbook.customXmlParts.add(newMetadataXml);
        newMetadataPart.load("id");
        await context.sync();

        namedRange.comment = newMetadataPart.id;
        await context.sync();
        console.log("newMetadataPart", newMetadataPart);

        const parsedUpdatedMetadata = CustomXmlPartSchema.parse(newMetadata);
        console.log("parsedUpdatedMetadata", parsedUpdatedMetadata);
        setMetadata(parsedUpdatedMetadata);

        // Update the selected range with the content
        const rangeAddress = context.workbook.getSelectedRange();
        rangeAddress.load("address");
        await context.sync();

        rangeAddress.values = [[snippedArea.content]];
        console.log("rangeAddress", rangeAddress);
        await context.sync();
        return;
      }
      try {
        // Get all customXmlParts
        const customXmlParts = context.workbook.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        for (const part of customXmlParts.items) {
          const xmlResult = await part.getXml();
          await context.sync();

          const xml = xmlResult.value;
          try {
            // Use xmlToJson utility to properly parse the XML
            const parsedContent = xmlToJson(xml);

            // Check if this is the XML part we want to update
            if (typeof parsedContent === "object" && parsedContent !== null) {
              // Directly check if this is the XML part with our metadata ID
              if ("id" in parsedContent && parsedContent.id === metadata?.id) {
                const namespaceMatch = xml.match(/xmlns:([^=]+)="([^"]+)"/);
                const namespace = namespaceMatch ? namespaceMatch[2] : "http://app.midship.ai";

                // Merge the updated metadata with the original object
                const updatedObject = {
                  ...parsedContent,
                  ...updatedMetadata,
                };

                // Convert back to XML and update the part
                const updatedXml = jsonToXmlString(updatedObject as any, "metadata", namespace, true, true);
                part.setXml(updatedXml);
                await context.sync();

                const parsedUpdatedMetadata = CustomXmlPartSchema.parse(updatedMetadata);
                setMetadata(parsedUpdatedMetadata);

                // Update the selected range with the content
                const range = context.workbook.getSelectedRange();
                range.load("address");
                await context.sync();

                range.values = [[snippedArea.content]];
                await context.sync();
                break;
              }
            }
          } catch (e) {
            console.error("Error parsing XML content:", e);
            continue;
          }
        }

        await context.sync();
      } catch (error) {
        console.error("Error updating XML part:", error);
        setError("Error updating XML part with new bounding box");
      }
    });
  };

  useEffect(() => {
    checkSelectedRange();
  }, []);

  const checkSelectedRange = async () => {
    setCurrentDocument(null);
    setCurrentChecks(null);
    setCurrentCellsSourceRowId(null);
    setLoading(true);
    setError(null);
    const startTime = performance.now();
    try {
      let foundMetadata = null;
      await Excel.run(async (context) => {
        // Get the selected range
        const range = context.workbook.getSelectedRange();
        range.load("address");
        await context.sync();

        const rangeAddress = range.address;
        console.log("Selected range address:", rangeAddress);

        // Load all named items to find if our selected range matches any
        const namedItems = context.workbook.names;
        namedItems.load("items/name,items/type");
        await context.sync();

        const validRanges = [];
        const validRangeNames = [];
        const rangeNamesAndAddresses: Record<string, string> = {};

        // First, filter out named items that are not ranges or are invalid
        for (const item of namedItems.items) {
          // Only process items that are of type "Range"
          if (item.type === "Range") {
            try {
              const namedItem = context.workbook.names.getItem(item.name);
              const namedRange = namedItem.getRangeOrNullObject();
              namedRange.load("address");
              validRanges.push(namedRange);
              validRangeNames.push(item.name);
            } catch (error) {
              console.error(`Error queueing named range ${item.name}:`, error);
            }
          } else {
            console.log(`Skipping named item ${item.name} - type: ${item.type} (not a range)`);
          }
        }

        await context.sync();

        for (let i = 0; i < validRanges.length; i++) {
          const rangeObj = validRanges[i];
          const rangeName = validRangeNames[i];

          if (!rangeObj.isNullObject) {
            rangeNamesAndAddresses[rangeName] = rangeObj.address;
          } else {
            console.log(`Named range ${rangeName} is invalid (null object), skipping`);
          }
        }

        // Find named item that matches the selected range address
        let namedRangeName = null;
        for (const [rangeName, rangeAddr] of Object.entries(rangeNamesAndAddresses)) {
          if (rangeAddr === rangeAddress) {
            namedRangeName = rangeName;
            break;
          }
        }

        if (!namedRangeName) {
          console.log("No matching named range found for address:", rangeAddress);
          setMetadata(null);
          setCurrentDocument(null);
          return;
        }

        console.log("Found matching named range:", namedRangeName);

        // Load all XML parts to find the one with our metadata
        const customXmlParts = context.workbook.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        // Queue up all XML part getXml operations without syncing
        const xmlPartPromises = customXmlParts.items.map((part) => {
          return part.getXml();
        });

        // Execute all operations in one batch
        await context.sync();

        const tempNamedRangeAddressToXmlPart: Record<string, CustomXmlPart> = {};

        // Process XML content after sync
        for (let i = 0; i < customXmlParts.items.length; i++) {
          try {
            const xmlResult = xmlPartPromises[i];
            const xml = xmlResult.value;
            const parsedContent = xmlToJson(xml);

            if (typeof parsedContent === "object" && parsedContent !== null) {
              if ("id" in parsedContent && typeof parsedContent.id === "string") {
                tempNamedRangeAddressToXmlPart[parsedContent.id] = parsedContent;
                if (parsedContent.id === namedRangeName) {
                  foundMetadata = parsedContent;
                  break;
                }
              }
            }
          } catch (e) {
            console.error("Error parsing XML content:", e);
            continue;
          }
        }
        getCurrentCellsSourceRowId({
          currentRangeAddress: rangeAddress,
          rangeNamesAndAddresses,
          namedRangeAddressToXmlPart: tempNamedRangeAddressToXmlPart,
        });
      }).catch((error) => {
        console.error("Excel.run error:", error);
      });

      console.log("foundMetadata", foundMetadata);
      if (foundMetadata) {
        const parsedFoundMetadata = CustomXmlPartSchema.parse(foundMetadata);
        setMetadata(parsedFoundMetadata);
      }

      await Promise.all([
        handleFileSelection(foundMetadata?.source_document?.id),
        handleCheckResultFetching(foundMetadata),
      ]);

      const endTime = performance.now();
      console.log(`Time taken: ${endTime - startTime} milliseconds`);
    } catch (error) {
      console.error("Error reading named range:", error);
      setError("Error reading named range data");
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  };

  // Check for named range whenever selection changes
  React.useEffect(() => {
    const handleSelectionChanged = () => {
      checkSelectedRange();
    };

    // Add event handler for selection change
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handleSelectionChanged);

    // Cleanup
    return () => {
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, handleSelectionChanged);
    };
  }, []); // No dependencies needed anymore

  console.log("metadata", metadata);
  console.log("currentChecks", currentChecks);

  const handleSnippingComplete = (snippedArea: {
    content?: string;
    bounding_box: [number, number][];
    pageNumber: number;
    page_width: number;
    page_height: number;
  }) => {
    const updatedMetadata = {
      ...metadata,
      source_document: {
        id: currentDocument?.documentExtraction.id,
        name: currentDocument?.documentExtraction.filename,
        bounding_box: snippedArea.bounding_box,
        page_number: snippedArea.pageNumber,
        page_width: snippedArea.page_width,
        page_height: snippedArea.page_height,
      },
    };

    // Update the XML part with the new bounding box information
    updateXmlPartWithBoundingBox(updatedMetadata, snippedArea);
    handleAddDocumentToSourceRow(currentDocument?.documentExtraction.id);
  };

  const isCsv = useMemo(() => currentDocument?.document.fileName?.endsWith(".csv"), [currentDocument]);
  const isImage = useMemo(
    () =>
      currentDocument?.document.fileName?.endsWith(".png") ||
      currentDocument?.document.fileName?.endsWith(".jpg") ||
      currentDocument?.document.fileName?.endsWith(".jpeg") ||
      currentDocument?.document.fileName?.endsWith(".webp"),
    [currentDocument]
  );
  const isPdf = useMemo(() => currentDocument?.document.fileName?.toLowerCase().endsWith(".pdf"), [currentDocument]);
  console.log("currentDocument", currentDocument);

  if (error) {
    return (
      <div className="p-2 h-full flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center justify-center gap-4">
          <Text>There was an error loading this cell</Text>
          <Text>Please try again</Text>
        </div>
      </div>
    );
  }
  if (!auditSession) {
    return (
      <div className="p-2">
        <Text>No audit session found</Text>
      </div>
    );
  }

  if (loading || auditSessionLoading || sessionXmlLoading) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white z-50">
        <Spinner />
        <Text className="font-medium">Finding linked document...</Text>
      </div>
    );
  }
  if (currentChecks) {
    return (
      <div className="h-[calc(100vh-40px)] relative overflow-y-auto">
        <CheckResultCard
          currentChecks={currentChecks}
          onUpdateCheckResult={setCurrentChecks}
          sourceRowId={metadata?.test_result?.sample_id}
          sessionId={metadata?.test_result?.session_id}
          checkId={currentChecks?.checkId}
          evalColumnId={currentChecks?.evalColumnId}
          evalColumn={currentChecks?.evalColumn}
        />
      </div>
    );
  }
  return (
    <div className="h-[calc(100vh-40px)] relative overflow-y-auto">
      <div className="p-2">
        <div className="flex flex-row flex-wrap gap-2 overflow-x-hidden">
          <SessionFileSearch
            session={auditSession}
            onSelectedFileChange={handleFileSelection}
            currentFileDocumentId={currentDocument?.documentExtraction.id}
          />
          <div className="flex-1">
            <SessionPlainTextSearch session={auditSession} onSelectedFileChange={handleFileSelection} />
          </div>
          <UploadFilesToSession />
        </div>
        {isCsv && (
          <div className="p-2">
            <CSVViewer
              url={currentDocument?.url || null}
              visible={!!currentDocument && !loading && isCsv}
              fileName={currentDocument?.document.fileName}
            />
          </div>
        )}
        {isImage && <ImageViewer signedUrl={currentDocument?.url || null} alt={currentDocument?.document.fileName} />}
        {!isCsv && !isImage && isPdf && (
          <PDFViewer
            initialPage={
              metadata?.source_document && (metadata.source_document as SourceDocument).page_number
                ? (metadata.source_document as SourceDocument).page_number
                : 1
            }
            url={currentDocument?.url || null}
            sourceDocument={
              metadata?.source_document && currentDocument?.documentExtraction && metadata.source_document
            }
            fileName={currentDocument?.document.fileName}
            visible={!!currentDocument && !loading && !isCsv}
            currentDocumentExtraction={currentDocument?.documentExtraction}
            onSnippingComplete={handleSnippingComplete}
          />
        )}
      </div>

      {/* Show default message when neither URL nor checks are available */}
      {!loading && !currentDocument && !currentChecks && (
        <div className="p-2 h-full flex flex-col items-center justify-center gap-4">
          <DotLottieReact src="assets/missingLink.lottie" loop autoplay style={{ width: "200px", height: "200px" }} />
          <Text>No link available for this cell</Text>
        </div>
      )}
    </div>
  );
};

export default NamedRangeReader;
